/**
 * NovoDFDPage.tsx — formulário do DFD (F5), réplica do new_dfd.html.
 *
 * Mantém a estrutura do original: página ÚNICA de rolagem com as seções
 * numeradas (Informações Gerais, Diagnóstico/Modalidade, Orçamento,
 * 1. Justificativa, 2. Quantitativo, 3. Disponibilidade, 4. PCA,
 * 5. Encaminhamento) — não é um wizard.
 *
 * Esta fatia cobre os campos, as regras condicionais e o rascunho. O
 * upload de planilha e a geração seguem no fluxo Jinja até a próxima.
 */

import {
  faArrowLeft,
  faBan,
  faBolt,
  faChartLine,
  faCheckCircle,
  faClipboardList,
  faCubes,
  faExclamationCircle,
  faExclamationTriangle,
  faFastForward,
  faGavel,
  faIdCard,
  faInfoCircle,
  faLightbulb,
  faLock,
  faMagic,
  faSave,
  faSpinner,
  faTools,
  faTruck,
  faUnlock,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { CampoSelect, CampoTexto, CampoTextarea, TituloSecao } from './campos'
import { PlanilhaItens } from './PlanilhaItens'
import { SelecaoPCA } from './SelecaoPCA'
import { opcoesArt79, useFormularioDFD } from './useFormularioDFD'

const CORES_TIPO_OBJETO: Record<string, string> = {
  green: 'text-green-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
  blue: 'text-blue-600',
  pink: 'text-pink-600',
  amber: 'text-amber-600',
}

function Secao({ children }: { children: React.ReactNode }) {
  return <div className="mb-8">{children}</div>
}

export function NovoDFDPage() {
  const [parametros] = useSearchParams()
  const idInicial = parametros.get('licitacao_id')
  const f = useFormularioDFD(idInicial ? Number(idInicial) : null)
  const [gerando, setGerando] = useState(false)
  const [erroGeracao, setErroGeracao] = useState<string | null>(null)

  if (!f.configuracao || f.carregandoRascunho) {
    return (
      <div className="p-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando formulário…
      </div>
    )
  }

  const c = f.configuracao
  const v = f.valores
  const vis = f.visibilidade
  const hipotese79 = String(v.hipotese_credenciamento ?? 'paralela')
  const art79 = opcoesArt79(hipotese79)
  const campo = (nome: string) => ({
    valor: v[nome] ?? '',
    aoMudar: (valor: string) => f.definir(nome, valor),
    erro: f.erros[nome],
    aoSair: f.agendarAutosave,
  })
  const areaTexto = (nome: string) => ({
    ...campo(nome),
    sugerivel: c.campos_sugeriveis.includes(nome),
    aoFocar: () => void f.sugerir(nome),
    sugerindo: f.campoSugerindo === nome,
  })

  return (
    <div className="mx-auto max-w-6xl">
      {/* Cabeçalho */}
      <div className="mb-6 text-center">
        <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faClipboardList} className="mr-3 text-green-600" />
          Documento de Formalização da Demanda (DFD)
        </h1>
        <p className="mt-2 text-slate-500 dark:text-slate-400">Lei 14.133/2021</p>
        {f.licitacaoId && (
          <span className="mt-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            <FontAwesomeIcon icon={faSave} />
            Rascunho #{f.licitacaoId} em edição
          </span>
        )}
      </div>

      {/* Dica de sugestões com IA */}
      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4 shadow-sm md:flex-row md:items-center md:justify-between dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
        <p className="text-sm text-slate-700 dark:text-slate-200">
          <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-indigo-600" />
          <strong>Dica:</strong> ao clicar num campo de texto vazio, a LicitIA
          sugere um conteúdo inicial a partir do objeto da contratação.
        </p>
        <button
          type="button"
          onClick={f.alternarSugestoes}
          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
            f.sugestoesAtivas
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-slate-400 hover:bg-slate-500'
          }`}
        >
          <FontAwesomeIcon icon={f.sugestoesAtivas ? faMagic : faBan} />
          {f.sugestoesAtivas ? 'Sugestões ativadas' : 'Sugestões desativadas'}
        </button>
      </div>

      {erroGeracao && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200"
        >
          <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5" />
          <span>{erroGeracao}</span>
        </div>
      )}

      <div className="rounded-2xl bg-white p-8 shadow-xl dark:bg-dark-card">
        {/* ── 1. Informações Gerais ─────────────────────────────────── */}
        <Secao>
          <TituloSecao icone={faInfoCircle} cor="text-green-600">
            Informações Gerais
          </TituloSecao>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <CampoTexto
                nome="objeto_contratacao"
                rotulo="Objeto da Contratação"
                obrigatorio
                placeholder="Descreva o objeto da contratação"
                {...campo('objeto_contratacao')}
              />
            </div>
            <CampoTexto nome="numero_processo" rotulo="Número do Processo" obrigatorio
              placeholder="Ex: 2024/001 ou SEI-123456" {...campo('numero_processo')} />
            <CampoTexto nome="municipio" rotulo="Município" obrigatorio
              placeholder="Nome do município" {...campo('municipio')} />
            <CampoTexto nome="orgao_solicitante" rotulo="Órgão Solicitante" obrigatorio
              placeholder="Nome do órgão" {...campo('orgao_solicitante')} />
            <CampoTexto nome="servidor_responsavel" rotulo="Servidor Responsável pelo DFD"
              obrigatorio placeholder="Nome completo do servidor"
              {...campo('servidor_responsavel')} />
            <CampoTexto nome="cpf_servidor" rotulo="CPF do Servidor" maxLength={14}
              placeholder="000.000.000-00" {...campo('cpf_servidor')} />
            <CampoTexto nome="matricula_servidor" rotulo="Matrícula do Servidor"
              placeholder="Ex: 12345" {...campo('matricula_servidor')} />
          </div>
        </Secao>

        {/* ── 2. Diagnóstico / Modalidade ───────────────────────────── */}
        <Secao>
          <TituloSecao icone={faGavel} cor="text-indigo-600">
            Diagnóstico de Necessidade — Modalidade de Licitação
          </TituloSecao>

          {/* Tipo de objeto */}
          <div className="mb-6 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
            <p className="mb-4 font-semibold text-slate-700 dark:text-slate-200">
              <FontAwesomeIcon icon={faCubes} className="mr-2 text-indigo-600" />
              Tipo de Objeto <span className="text-red-500">*</span>
            </p>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {c.tipos_objeto.map((t) => (
                <label
                  key={t.valor}
                  className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                    v.tipo_objeto === t.valor
                      ? 'border-indigo-500 bg-indigo-100 ring-2 ring-indigo-500 dark:bg-indigo-900/40'
                      : 'border-indigo-200 hover:bg-indigo-100 dark:border-indigo-800 dark:hover:bg-indigo-900/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo_objeto"
                    value={t.valor}
                    checked={v.tipo_objeto === t.valor}
                    onChange={(e) => {
                      f.definir('tipo_objeto', e.target.value)
                      f.agendarAutosave()
                    }}
                    className="sr-only"
                  />
                  <span className={`mb-1 block font-semibold ${CORES_TIPO_OBJETO[t.cor] ?? 'text-slate-700'}`}>
                    {t.rotulo}
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    {t.descricao}
                  </span>
                </label>
              ))}
            </div>

            {/* R1 — serviço COMUM de engenharia (Art. 6º, XXI "a") */}
            {vis.servicoComumEngenharia && (
              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                <input
                  type="checkbox"
                  checked={v.servico_comum_engenharia === 'on'}
                  onChange={(e) => {
                    f.definir('servico_comum_engenharia', e.target.checked ? 'on' : '')
                    f.agendarAutosave()
                  }}
                  className="mt-1 h-4 w-4 accent-amber-600"
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  <FontAwesomeIcon icon={faTools} className="mr-2 text-amber-600" />
                  <strong>Serviço COMUM de Engenharia</strong> (Art. 6º, XXI, “a”) —
                  admite Pregão em vez de Concorrência.
                </span>
              </label>
            )}
          </div>

          {/* Contratação direta (Art. 74/75) */}
          <div className="mb-6 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6 dark:border-amber-800 dark:from-amber-900/20 dark:to-orange-900/20">
            <p className="mb-4 font-semibold text-amber-700 dark:text-amber-400">
              <FontAwesomeIcon icon={faFastForward} className="mr-2" />
              Contratação Direta (Art. 74/75)
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                { valor: 'licitacao', rotulo: 'Licitação', badge: 'Padrão', icone: faGavel, cor: 'blue' },
                { valor: 'inexigibilidade', rotulo: 'Inexigibilidade', badge: 'Art. 74', icone: faUserTie, cor: 'amber' },
                { valor: 'dispensa', rotulo: 'Dispensa', badge: 'Art. 75', icone: faBolt, cor: 'orange' },
              ].map((o) => {
                const ativo = (v.tipo_contratacao_direta ?? 'licitacao') === o.valor
                const cores: Record<string, string> = {
                  blue: ativo ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : '',
                  amber: ativo ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30' : '',
                  orange: ativo ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30' : '',
                }
                const iconeCor: Record<string, string> = {
                  blue: 'text-blue-600', amber: 'text-amber-600', orange: 'text-orange-600',
                }
                return (
                  <label
                    key={o.valor}
                    className={`cursor-pointer rounded-lg border-2 p-4 text-center transition-colors ${
                      ativo ? cores[o.cor] : 'border-slate-200 hover:bg-white/60 dark:border-slate-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tipo_contratacao_direta"
                      value={o.valor}
                      checked={ativo}
                      onChange={(e) => {
                        f.definir('tipo_contratacao_direta', e.target.value)
                        f.agendarAutosave()
                      }}
                      className="sr-only"
                    />
                    <FontAwesomeIcon icon={o.icone} className={`mb-2 text-2xl ${iconeCor[o.cor]}`} />
                    <span className="block font-semibold text-slate-700 dark:text-slate-200">
                      {o.rotulo}
                    </span>
                    <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500 dark:bg-slate-700">
                      {o.badge}
                    </span>
                  </label>
                )
              })}
            </div>

            {vis.contratacaoDireta && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-100 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                Em contratação direta não há escolha de modalidade nem orçamento
                sigiloso — a fundamentação é a hipótese legal invocada.
              </div>
            )}

            {/* Inexigibilidade (Art. 74) */}
            {vis.inexigibilidade && (
              <div className="mt-4 space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-900/20">
                <p className="font-semibold text-amber-700 dark:text-amber-400">
                  <FontAwesomeIcon icon={faUserTie} className="mr-2" />
                  Configuração da Inexigibilidade (Art. 74)
                </p>
                <CampoSelect
                  nome="hipotese_inexigibilidade"
                  rotulo="Hipótese de Inexigibilidade"
                  obrigatorio
                  opcoes={c.hipoteses_inexigibilidade}
                  vazio="Selecione a hipótese..."
                  {...campo('hipotese_inexigibilidade')}
                />
                <CampoTextarea
                  nome="justificativa_inexigibilidade"
                  rotulo="Justificativa da Inexigibilidade"
                  obrigatorio
                  placeholder="Descreva detalhadamente por que a competição é inviável para este objeto..."
                  {...areaTexto('justificativa_inexigibilidade')}
                />

                {/* Credenciamento (Art. 79) */}
                {vis.credenciamento && (
                  <div className="space-y-4 rounded-lg border-2 border-teal-300 bg-teal-50 p-5 dark:border-teal-700 dark:bg-teal-900/20">
                    <p className="font-semibold text-teal-700 dark:text-teal-400">
                      <FontAwesomeIcon icon={faIdCard} className="mr-2" />
                      Configuração do Credenciamento (Art. 79)
                    </p>
                    <div className="rounded-lg border border-teal-200 bg-white/70 p-3 text-sm text-teal-800 dark:border-teal-800 dark:bg-teal-900/30 dark:text-teal-200">
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                      A fundamentação do credenciamento é a <strong>pluralidade</strong>:
                      é vantajoso contratar todos os interessados aptos — nunca a
                      exclusividade.
                    </div>
                    <fieldset>
                      <legend className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Hipótese de Credenciamento (Art. 79) <span className="text-red-500">*</span>
                      </legend>
                      <div className="space-y-2">
                        {c.hipoteses_credenciamento.map((h) => (
                          <label key={h.valor} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                            <input
                              type="radio"
                              name="hipotese_credenciamento"
                              value={h.valor}
                              checked={(v.hipotese_credenciamento ?? 'paralela') === h.valor}
                              onChange={(e) => {
                                f.definir('hipotese_credenciamento', e.target.value)
                                f.agendarAutosave()
                              }}
                              className="h-4 w-4 accent-teal-600"
                            />
                            {h.rotulo}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <CampoSelect nome="forma_distribuicao_demanda"
                      rotulo="Forma de Distribuição da Demanda" obrigatorio
                      opcoes={c.formas_distribuicao.filter((o) => art79.distribuicao.includes(o.valor))}
                      {...campo('forma_distribuicao_demanda')}
                      desabilitado={art79.distribuicao.length === 1}
                      ajuda={hipotese79 === 'terceiros'
                        ? 'Fixada pela hipótese do Art. 79, II: a seleção é do beneficiário. Para rodízio ou ordem de demanda, use o Art. 79, I.'
                        : 'Livre escolha pelo beneficiário é a hipótese do Art. 79, II.'} />
                    <CampoSelect nome="origem_tabela_precos"
                      rotulo="Remuneração / Tabela de Preços" obrigatorio
                      opcoes={c.origens_tabela_precos.filter((o) => art79.remuneracao.includes(o.valor))}
                      {...campo('origem_tabela_precos')}
                      desabilitado={art79.remuneracao.length === 1}
                      ajuda={hipotese79 === 'mercados_fluidos'
                        ? 'Em mercados fluidos (Art. 79, III) vale a cotação de mercado no momento da contratação (§1º, IV).'
                        : 'Nas hipóteses I e II o edital define o valor da contratação (Art. 79, §1º, III).'} />
                    {vis.referencialTabela && (
                      <CampoTexto nome="referencial_tabela_precos"
                        rotulo="Qual tabela referencial?" obrigatorio
                        placeholder="Ex.: Tabela SUS (SIGTAP), SINAPI, CMED..."
                        {...campo('referencial_tabela_precos')} />
                    )}
                    <CampoTexto nome="vigencia_credenciamento" rotulo="Vigência do Credenciamento"
                      placeholder="Ex.: 12 meses, prorrogável, com inscrições permanentemente abertas"
                      {...campo('vigencia_credenciamento')} />
                    <CampoTextarea nome="justificativa_credenciamento"
                      rotulo="Justificativa da Escolha do Credenciamento" obrigatorio linhas={3}
                      placeholder="Ex.: Há pluralidade de prestadores aptos e é vantajoso contratar todos os interessados que atendam aos requisitos..."
                      {...areaTexto('justificativa_credenciamento')} />
                  </div>
                )}
              </div>
            )}

            {/* Dispensa (Art. 75) */}
            {vis.dispensa && (
              <div className="mt-4 space-y-4 rounded-lg border border-orange-200 bg-orange-50 p-5 dark:border-orange-800 dark:bg-orange-900/20">
                <p className="font-semibold text-orange-700 dark:text-orange-400">
                  <FontAwesomeIcon icon={faBolt} className="mr-2" />
                  Configuração da Dispensa (Art. 75)
                </p>
                <div>
                  <label htmlFor="hipotese_dispensa" className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Hipótese de Dispensa <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="hipotese_dispensa"
                    value={v.hipotese_dispensa ?? ''}
                    onChange={(e) => {
                      f.definir('hipotese_dispensa', e.target.value)
                      f.agendarAutosave()
                    }}
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-green-500 dark:border-dark-border dark:bg-dark-bg dark:text-slate-100"
                  >
                    <option value="">Selecione a hipótese...</option>
                    {c.hipoteses_dispensa.map((g) => (
                      <optgroup key={g.grupo} label={g.grupo}>
                        {g.opcoes.map((o) => (
                          <option key={o.valor} value={o.valor}>{o.rotulo}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {f.erros.hipotese_dispensa && (
                    <p className="mt-1 text-xs text-red-600">{f.erros.hipotese_dispensa}</p>
                  )}
                </div>
                {vis.incisoDispensa && (
                  <CampoTexto nome="inciso_dispensa" rotulo="Especifique o inciso do Art. 75"
                    obrigatorio placeholder="Ex: XVI, XVII, XVIII..."
                    {...campo('inciso_dispensa')} />
                )}
                {vis.avisoEmergencia && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                    Emergência exige demonstrar a urgência concreta e limitar o
                    objeto ao necessário para afastar o risco (Art. 75, VIII).
                  </div>
                )}
                <CampoTextarea nome="justificativa_dispensa" rotulo="Justificativa da Dispensa"
                  obrigatorio placeholder="Descreva a situação que fundamenta a dispensa de licitação..."
                  {...areaTexto('justificativa_dispensa')} />
              </div>
            )}
          </div>

          {/* Modalidade */}
          {!vis.contratacaoDireta && f.regra && (
            <div className="rounded-lg border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 p-6 dark:border-green-800 dark:from-green-900/20 dark:to-emerald-900/20">
              <p className="mb-4 font-semibold text-green-700 dark:text-green-400">
                <FontAwesomeIcon icon={faGavel} className="mr-2" />
                Modalidade de Licitação
              </p>

              {vis.modalidadeObrigatoria ? (
                <div className="rounded-lg border border-green-300 bg-green-100 p-4 dark:border-green-700 dark:bg-green-900/30">
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-600" />
                  <strong className="text-slate-800 dark:text-slate-100">
                    {c.modalidades[f.regra.principal]?.nome}
                  </strong>
                  <span className="ml-2 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                    Obrigatória
                  </span>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {c.modalidades[f.regra.principal]?.descricao}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {[f.regra.principal, ...f.regra.alternativas].map((m) => (
                    <label
                      key={m}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
                        v.modalidade_licitacao === m
                          ? 'border-green-500 bg-green-100 dark:bg-green-900/30'
                          : 'border-green-200 hover:bg-green-100 dark:border-green-800 dark:hover:bg-green-900/20'
                      }`}
                    >
                      <input
                        type="radio"
                        name="modalidade_licitacao"
                        value={m}
                        checked={v.modalidade_licitacao === m}
                        onChange={(e) => {
                          f.definir('modalidade_licitacao', e.target.value)
                          f.agendarAutosave()
                        }}
                        className="h-4 w-4 accent-green-600"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-200">
                        <strong>{c.modalidades[m]?.nome}</strong> — {c.modalidades[m]?.descricao}
                      </span>
                      {m === f.regra!.principal && (
                        <span className="ml-auto rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                          Principal
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}

              {vis.justificativaModalidade && (
                <div className="mt-4">
                  <CampoTextarea nome="justificativa_modalidade"
                    rotulo="Justificativa para Modalidade Alternativa" obrigatorio linhas={3}
                    placeholder="Justifique a escolha da modalidade alternativa..."
                    {...areaTexto('justificativa_modalidade')} />
                </div>
              )}

              {vis.permiteSrp && (
                <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                  <input
                    type="checkbox"
                    checked={v.usar_sistema_registro_precos === 'on'}
                    onChange={(e) => {
                      f.definir('usar_sistema_registro_precos', e.target.checked ? 'on' : '')
                      f.agendarAutosave()
                    }}
                    className="mt-1 h-4 w-4 accent-blue-600"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-200">
                    <FontAwesomeIcon icon={faClipboardList} className="mr-2 text-blue-600" />
                    <strong>Utilizar Sistema de Registro de Preços (SRP/ARP)</strong>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      Para contratações futuras com entregas parceladas (Arts. 82-86)
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
        </Secao>

        {/* ── 3. Caráter do Orçamento ───────────────────────────────── */}
        {!vis.contratacaoDireta && (
          <Secao>
            <TituloSecao icone={faLock} cor="text-purple-600">
              Caráter do Orçamento (Art. 18, §1º, VI)
            </TituloSecao>
            <div className="rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 p-6 dark:border-purple-800 dark:from-purple-900/20 dark:to-indigo-900/20">
              <div className="grid gap-4 md:grid-cols-2">
                {[
                  { valor: 'publico', rotulo: 'Orçamento Público', badge: 'Padrão', icone: faUnlock, ativo: 'border-green-500 bg-green-50 dark:bg-green-900/30' },
                  { valor: 'sigiloso', rotulo: 'Orçamento Sigiloso', badge: 'Justificativa obrigatória', icone: faLock, ativo: 'border-purple-500 bg-purple-50 dark:bg-purple-900/30' },
                ].map((o) => {
                  const ativo = (v.orcamento_sigiloso ?? 'publico') === o.valor
                  return (
                    <label
                      key={o.valor}
                      className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                        ativo ? o.ativo : 'border-slate-200 hover:bg-white/60 dark:border-slate-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="orcamento_sigiloso"
                        value={o.valor}
                        checked={ativo}
                        onChange={(e) => {
                          f.definir('orcamento_sigiloso', e.target.value)
                          f.agendarAutosave()
                        }}
                        className="sr-only"
                      />
                      <FontAwesomeIcon
                        icon={o.icone}
                        className={`mb-2 text-2xl ${o.valor === 'publico' ? 'text-green-600' : 'text-purple-600'}`}
                      />
                      <span className="block font-semibold text-slate-700 dark:text-slate-200">
                        {o.rotulo}
                      </span>
                      <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500 dark:bg-slate-700">
                        {o.badge}
                      </span>
                    </label>
                  )
                })}
              </div>

              {vis.sigilo && (
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                    O sigilo é vedado em contratação integrada e não alcança os
                    órgãos de controle, que têm acesso irrestrito.
                  </div>
                  <CampoTextarea nome="justificativa_sigilo"
                    rotulo="Justificativa Técnico-Econômica para o Sigilo" obrigatorio
                    placeholder="Descreva os critérios técnico-econômicos que justificam a opção pelo sigilo do orçamento..."
                    {...areaTexto('justificativa_sigilo')} />
                </div>
              )}
            </div>
          </Secao>
        )}

        {/* ── 4. Justificativa ──────────────────────────────────────── */}
        <Secao>
          <TituloSecao icone={faClipboardList} cor="text-green-600">
            1. Justificativa
          </TituloSecao>
          <div className="space-y-6">
            <CampoTextarea nome="identificacao_demanda" rotulo="Identificação da Demanda"
              obrigatorio placeholder="Identifique claramente a demanda..."
              {...areaTexto('identificacao_demanda')} />
            <CampoTextarea nome="justificativa_necessidade" rotulo="Justificativa da Necessidade"
              obrigatorio linhas={6} placeholder="Justifique a necessidade da contratação..."
              {...areaTexto('justificativa_necessidade')} />
            <CampoTextarea nome="descricao_necessidade" rotulo="Descrição da Necessidade"
              placeholder="Descreva detalhadamente a natureza e o escopo do que precisa ser contratado..."
              {...areaTexto('descricao_necessidade')} />
            <CampoTextarea nome="problematica_enfrentada" rotulo="Problemática Enfrentada"
              placeholder="Descreva os problemas, deficiências ou carências que motivam esta contratação..."
              {...areaTexto('problematica_enfrentada')} />
            <CampoTextarea nome="resultados_almejados" rotulo="Resultados Almejados"
              obrigatorio placeholder="Descreva os resultados esperados..."
              {...areaTexto('resultados_almejados')} />
          </div>
        </Secao>

        {/* ── 5. Quantitativo ───────────────────────────────────────── */}
        <Secao>
          <TituloSecao icone={faCubes} cor="text-green-600">
            2. Do Quantitativo
          </TituloSecao>
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <p className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
              Tipo de Contratação
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              {[
                { valor: 'material', rotulo: 'Materiais', sub: 'Busca códigos CATMAT' },
                { valor: 'servico', rotulo: 'Serviços', sub: 'Busca códigos CATSER' },
                { valor: 'misto', rotulo: 'Misto', sub: 'Materiais + Serviços' },
              ].map((o) => (
                <label
                  key={o.valor}
                  className={`cursor-pointer rounded-lg border-2 p-3 text-center transition-colors ${
                    (v.tipo_contratacao ?? 'material') === o.valor
                      ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/40'
                      : 'border-slate-200 hover:bg-white/60 dark:border-slate-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo_contratacao"
                    value={o.valor}
                    checked={(v.tipo_contratacao ?? 'material') === o.valor}
                    onChange={(e) => {
                      f.definir('tipo_contratacao', e.target.value)
                      f.agendarAutosave()
                    }}
                    className="sr-only"
                  />
                  <span className="block font-semibold text-slate-700 dark:text-slate-200">
                    {o.rotulo}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{o.sub}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Planilhas de itens conforme o tipo escolhido */}
          <div className="mb-6 space-y-4">
            {(v.tipo_contratacao ?? 'material') !== 'servico' && (
              <PlanilhaItens
                tipo="material"
                usarTabelaSus={false}
                estado={f.planilhaMaterial}
                aoMudar={f.definirPlanilhaMaterial}
              />
            )}
            {(v.tipo_contratacao ?? 'material') !== 'material' && (
              <PlanilhaItens
                tipo="servico"
                usarTabelaSus={v.usar_tabela_sus === 'on'}
                aoAlternarSus={(marcado) =>
                  f.definir('usar_tabela_sus', marcado ? 'on' : '')
                }
                estado={f.planilhaServico}
                aoMudar={f.definirPlanilhaServico}
              />
            )}
          </div>
          <div className="space-y-6">
            <CampoTextarea nome="justificativa_quantidades" rotulo="Justificativa das Quantidades"
              obrigatorio placeholder="Justifique as quantidades solicitadas..."
              {...areaTexto('justificativa_quantidades')} />
            <CampoTextarea nome="metodologia_distribuicao" rotulo="Metodologia de Distribuição"
              obrigatorio placeholder="Descreva a metodologia de distribuição..."
              {...areaTexto('metodologia_distribuicao')} />
          </div>
        </Secao>

        {/* ── 6. Previsão de Disponibilidade ────────────────────────── */}
        <Secao>
          <TituloSecao icone={faTruck} cor="text-green-600">
            3. Previsão de Disponibilidade
          </TituloSecao>
          <div className="space-y-6">
            <CampoTextarea nome="forma_fornecimento" rotulo="Forma de Fornecimento" obrigatorio
              linhas={3} placeholder="Descreva a forma de fornecimento..."
              {...areaTexto('forma_fornecimento')} />
            <CampoTextarea nome="prazos_condicoes" rotulo="Prazos e Condições" obrigatorio
              linhas={3} placeholder="Informe prazos e condições..."
              {...areaTexto('prazos_condicoes')} />
            <CampoTextarea nome="local_entrega" rotulo="Local de Entrega" obrigatorio linhas={2}
              placeholder="Informe o local de entrega..." {...areaTexto('local_entrega')} />
            <CampoTextarea nome="condicoes_tecnicas" rotulo="Condições Técnicas" obrigatorio
              placeholder="Descreva as condições técnicas..." {...areaTexto('condicoes_tecnicas')} />
            <CampoTextarea nome="suporte_tecnico" rotulo="Suporte Técnico" obrigatorio linhas={3}
              placeholder="Descreva o suporte técnico necessário..." {...areaTexto('suporte_tecnico')} />
            <CampoTextarea nome="procedimentos_recebimento" rotulo="Procedimentos de Recebimento"
              obrigatorio placeholder="Descreva os procedimentos de recebimento..."
              {...areaTexto('procedimentos_recebimento')} />
          </div>
        </Secao>

        {/* ── 7. PCA ────────────────────────────────────────────────── */}
        <Secao>
          <TituloSecao icone={faChartLine} cor="text-green-600">
            4. Demonstrativo PCA (Plano de Contratações Anual)
          </TituloSecao>
          <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-dark-bg/40">
            <p className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
              Consta no PCA? <span className="text-red-500">*</span>
            </p>
            <div className="flex gap-6">
              {[{ valor: 'true', rotulo: 'Sim' }, { valor: 'false', rotulo: 'Não' }].map((o) => (
                <label key={o.valor} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="radio"
                    name="consta_pca"
                    value={o.valor}
                    checked={v.consta_pca === o.valor}
                    onChange={(e) => {
                      f.definir('consta_pca', e.target.value)
                      f.agendarAutosave()
                    }}
                    className="h-4 w-4 accent-green-600"
                  />
                  {o.rotulo}
                </label>
              ))}
            </div>
            {f.erros.consta_pca && (
              <p className="mt-2 text-xs text-red-600">{f.erros.consta_pca}</p>
            )}
          </div>
          <div className="space-y-6">
            {vis.justificativaPca && (
              <CampoTextarea nome="justificativa_pca"
                rotulo="Justificativa (se não consta no PCA)" obrigatorio
                placeholder="Justifique por que esta contratação não consta no PCA aprovado..."
                {...areaTexto('justificativa_pca')} />
            )}
            {vis.vinculoPca && (
              <>
                <SelecaoPCA
                  secretaria={v.pca_secretaria_filter ?? ''}
                  itemId={v.pca_item_id ?? ''}
                  aoMudar={(campo, valor) => {
                    f.definir(campo, valor)
                    f.agendarAutosave()
                  }}
                />
                <CampoTextarea nome="alinhamento_metas" rotulo="Alinhamento com Metas" linhas={3}
                  placeholder="Descreva o alinhamento com as metas..."
                  {...areaTexto('alinhamento_metas')} />
                <CampoTextarea nome="compatibilidade_orcamentaria"
                  rotulo="Compatibilidade Orçamentária" linhas={3}
                  placeholder="Descreva a compatibilidade orçamentária..."
                  {...areaTexto('compatibilidade_orcamentaria')} />
              </>
            )}
          </div>
        </Secao>

        {/* ── 8. Encaminhamento ─────────────────────────────────────── */}
        <Secao>
          <TituloSecao icone={faUserTie} cor="text-green-600">
            5. Encaminhamento
          </TituloSecao>
          <div className="grid gap-6 md:grid-cols-3">
            <CampoTexto nome="responsavel" rotulo="Responsável"
              placeholder="Nome do responsável" {...campo('responsavel')} />
            <CampoTexto nome="cargo" rotulo="Cargo" placeholder="Cargo do responsável"
              {...campo('cargo')} />
            <CampoTexto nome="data_elaboracao" rotulo="Data de Elaboração" tipo="date"
              {...campo('data_elaboracao')} />
          </div>
        </Secao>

        {/* Ações */}
        <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 md:flex-row md:justify-between dark:border-slate-700">
          <a
            href="/app/processos"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Voltar
          </a>
          <div className="flex flex-col gap-3 md:flex-row">
            <button
              type="button"
              onClick={f.salvarAgora}
              disabled={f.salvamento === 'salvando'}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={f.salvamento === 'salvando' ? faSpinner : faSave}
                spin={f.salvamento === 'salvando'} />
              Salvar Rascunho
            </button>
            <button
              type="button"
              disabled={gerando}
              onClick={async () => {
                setGerando(true)
                const problema = await f.gerar()
                if (problema) {
                  setErroGeracao(problema)
                  setGerando(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
                // sem problema: o form nativo navega para a página de status
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-blue-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-green-700 hover:to-blue-700 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={gerando ? faSpinner : faMagic} spin={gerando} />
              {gerando ? 'Gerando DFD com IA…' : 'Gerar Com LicitIA'}
            </button>
          </div>
        </div>
      </div>

      {/* Indicador de autosave (canto inferior direito, como no original) */}
      {f.salvamento !== 'ocioso' && (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm shadow-lg ${
            f.salvamento === 'erro'
              ? 'bg-red-100 text-red-800'
              : f.salvamento === 'salvo'
                ? 'bg-green-100 text-green-800'
                : 'bg-white text-slate-700 dark:bg-dark-card dark:text-slate-200'
          }`}
        >
          {f.salvamento === 'salvando' && (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Salvando rascunho…
            </>
          )}
          {f.salvamento === 'salvo' && (
            <>
              <FontAwesomeIcon icon={faCheckCircle} /> Rascunho salvo
            </>
          )}
          {f.salvamento === 'erro' && (
            <>
              <FontAwesomeIcon icon={faExclamationCircle} /> Erro ao salvar
            </>
          )}
        </div>
      )}
    </div>
  )
}
